<template>
  <div class="container-fluid">
    <div class="nk-content-inner">
      <!-- Back button -->
      <div class="mb-4">
        <router-link to="/" class="inline-flex items-center text-text-secondary hover:text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clip-rule="evenodd"/>
          </svg>
          Back to Home
        </router-link>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="py-8 flex justify-center">
        <div class="text-center">
          <svg class="animate-spin h-10 w-10 text-primary mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none"
               viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-text-secondary">Loading product details...</p>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="bg-background-light rounded-xl p-6">
        <div class="text-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-red-500 mb-4" fill="none"
               viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>

          <h2 class="text-lg font-bold mb-2">Product Not Found</h2>
          <p class="text-text-secondary mb-4">
            {{ error }}
          </p>

          <button @click="loadData()" class="btn btn-primary mr-2">
            Try Again
          </button>

          <router-link to="/" class="btn bg-background-lighter text-text">
            Return to Homepage
          </router-link>
        </div>
      </div>

      <!-- Main product content -->
      <div v-else-if="subcategory" class="nk-block">
        <div class="card card-bordered bg-background rounded-lg shadow-sm">
          <div class="card-inner">
            <!-- Product layout exactly like your reference -->
            <div class="product-layout">
              <!-- Left side: Huge image (like your reference) -->
              <div class="product-image-section">
                <div class="subcategory-image">
                  <div class="image-container">
                    <img
                        :src="getImageUrl(subcategory)"
                        :alt="subcategory.libelle"
                        @error="handleImageError"
                        class="main-product-image"
                    />
                  </div>
                </div>
              </div>

              <!-- Right side: Product info (like your reference) -->
              <div class="product-info-section">
                <div class="product-info">
                  <!-- Product title -->
                  <h1 class="product-title">{{ subcategory.libelle }}</h1>

                  <!-- Rating -->
                  <div class="product-rating">
                    <div class="stars">
                      <span v-for="i in 5" :key="'star-'+i" class="star">★</span>
                    </div>
                    <span class="rating-text">(5.0)</span>
                  </div>

                  <!-- Price display -->
                  <div v-if="selectedArticle" class="price-display">
                      <span class="current-price">
                        {{ formatPrice(selectedArticle.price || selectedArticle.prix) }}
                      </span>
                    <span v-if="selectedArticle.old_price || selectedArticle.prix_promo" class="old-price">
                        {{ formatPrice(selectedArticle.old_price || selectedArticle.prix_promo) }}
                      </span>
                  </div>

                  <!-- Country selection -->
                  <div v-if="countries.length > 0" class="country-selection">
                    <h6 class="section-title">Select Region/Country:</h6>
                    <div class="country-options">
                      <div v-for="country in countries" :key="country.id" class="country-option">
                        <input
                            type="radio"
                            class="country-input"
                            name="sizeCheck"
                            :id="'country-' + country.id"
                            :checked="selectedCountryId === country.id"
                            @click="selectCountry(country.id)"
                        >
                        <label
                            class="country-label"
                            :class="{ 'selected': selectedCountryId === country.id }"
                            :for="'country-' + country.id"
                        >
                          {{ country.name }}
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Article options with professional badges -->
                  <div class="article-selection">
                    <h6 class="section-title">Options:</h6>


                    <div class="articles-grid">
                      <div v-for="article in filteredArticles"
                           :key="article.id"
                           class="article-card"
                           :class="{ 'article-disabled': disabledArticlesMap[article.id] }">

                        <!-- Badge -->
                        <!-- Out of Stock Badge (highest priority) -->
                        <div v-if="getArticleStockCount(article) < 1 && !shouldShowAmountField(article) && !shouldShowPremiumField(article) && !article.message  && article.message !== ''"
                             class="out-of-stock-badge">
                          Out of Stock
                        </div>

                        <!-- Other Badges -->
                        <div v-else-if="getArticleBadge(article)" class="article-badge" :class="getBadgeClass(article)">
                          <div class="badge-content">
                            <span class="badge-icon" v-html="getBadgeIcon(article)"></span>
                            <span class="badge-text">{{ getArticleBadge(article) }}</span>
                          </div>
                        </div>


                        <!-- Article input and label -->
                        <input type="radio"
                               class="article-input"
                               name="articleType"
                               :id="'article-' + article.id"
                               :checked="selectedArticle && selectedArticle.id === article.id"
                               :disabled="disabledArticlesMap[article.id]"
                               @click="!disabledArticlesMap[article.id] && selectArticle(article)">

                        <label class="article-label"
                               :class="{
                   selected: selectedArticle && selectedArticle.id === article.id,
                   disabled: disabledArticlesMap[article.id]
               }"
                               :for="'article-' + article.id">
                          <div class="article-content">
                            <div class="article-text">
                              <span class="article-name">{{ article.libelle }}</span>
                              <div class="price-section">
                                <span class="article-price">{{ formatPrice(article.price || article.prix) }}</span>
                                <span v-if="article.oldprice || article.prixancien"
                                      class="article-old-price">
                {{ formatPrice(article.oldprice || article.prixancien) }}
            </span>
                              </div>
                            </div>

                            <!-- Small Article Icon - Now on the right -->
                            <img v-if="article.image_url"
                                 :src="`${article.image_url}`"
                                 :alt="article.libelle"
                                 class="article-icon"
                                 @error="handleImageError">
                          </div>

                        </label>
                      </div>
                    </div>

                  </div>

                  <!-- Quantity input -->
                  <div
                      v-if="selectedArticle && !selectedArticle.message && !shouldShowAmountField(selectedArticle) && !shouldShowPremiumField(selectedArticle)"
                      class="quantity-section">
                    <label class="section-title">Quantity:</label>
                    <div class="quantity-controls">
                      <button
                          @click="decrementQuantity"
                          class="quantity-btn"
                          :disabled="quantity <= 1"
                      >
                        -
                      </button>
                      <input
                          type="number"
                          v-model="quantity"
                          min="1"
                          :max="maxQuantity"
                          class="quantity-input"
                      >
                      <button
                          @click="incrementQuantity"
                          class="quantity-btn"
                          :disabled="quantity >= maxQuantity"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <!-- Player info input with FreeFire check -->
                  <div v-if="selectedArticle && selectedArticle.message" class="input-section">
                    <label class="section-title">{{ selectedArticle.message }}</label>

                    <!-- Input with check button (only for FreeFire articles with offer_id) -->
                    <div class="flex gap-2">
                      <input
                          type="text"
                          class="text-input"
                          :class="{
          'error': validationErrors.playerInfo,
          'success': playerCheckResult && playerCheckResult.success
        }"
                          v-model="playerInfo"
                          :placeholder="selectedArticle.message"
                          @input="resetPlayerCheck"
                          :disabled="isCheckingPlayer"
                      >

                      <!-- Check Player Button - Only show for articles with offer_id -->
                      <button
                          v-if="requiresPlayerCheck"
                          @click="checkPlayer"
                          :disabled="isCheckingPlayer || !playerInfo || playerInfo.trim() === ''"
                          class="btn-check-player"
                          :class="{ 'loading': isCheckingPlayer }"
                      >
                        <!-- Loading spinner -->
                        <svg v-if="isCheckingPlayer" class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>

                        <!-- Check icon -->
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>

                        <span class="ml-1">{{ isCheckingPlayer ? 'Checking...' : 'Check Player' }}</span>
                      </button>
                    </div>

                    <!-- Validation error message -->
                    <p v-if="validationErrors.playerInfo" class="error-text">
                      {{ validationErrors.playerInfo }}
                    </p>

                    <!-- Player check result display -->
                    <div v-if="playerCheckResult && requiresPlayerCheck" class="player-check-result" :class="playerCheckResult.type">
                      <!-- Success result -->
                      <div v-if="playerCheckResult.success" class="result-success">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        <div class="result-content">
                          <p class="result-title">Player Validated Successfully</p>
                          <div class="player-details">
                            <span><strong>Name:</strong> {{ playerCheckResult.player_name }}</span>
                            <span class="ml-4"><strong>Region:</strong> {{ playerCheckResult.region }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Error result -->
                      <div v-else class="result-error">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                        <div class="result-content">
                          <p class="result-title">{{ playerCheckResult.message }}</p>
                        </div>
                      </div>
                    </div>
                  </div>




                  <!-- Special pricing inputs -->
                  <div v-if="selectedArticle && shouldShowAmountField(selectedArticle)" class="input-section">
                    <label class="section-title">Enter games amount (TRY):</label>
                    <input
                        type="number"
                        class="text-input"
                        v-model="amount"
                        placeholder="Amount in Turkish Lira"
                        :class="{'error': validationErrors.amount}"
                        @input="calculateResult(amount)"
                    >
                    <p v-if="validationErrors.amount" class="error-text">
                      {{ validationErrors.amount }}
                    </p>
                    <div class="result-display" v-if="amount">
                      <div v-html="formattedResult"></div>
                    </div>
                  </div>

                  <!-- Premium pricing inputs -->
                  <div v-if="selectedArticle && shouldShowPremiumField(selectedArticle)" class="input-section">
                    <label class="section-title">Enter premium amount (TRY):</label>
                    <input
                        type="number"
                        class="text-input"
                        v-model="amount2"
                        placeholder="Amount in Turkish Lira"
                        :class="{'error': validationErrors.amount2}"
                        @input="calculateResult2(amount2)"
                    >
                    <p v-if="validationErrors.amount2" class="error-text">
                      {{ validationErrors.amount2 }}
                    </p>
                    <div class="result-display" v-if="amount2">
                      <div v-html="formattedResult2"></div>
                    </div>
                  </div>

                  <!-- Action buttons -->
                  <div class="action-buttons">
                    <button
                        v-if="selectedArticle&& !dataStore.shouldHideCart "
                        @click="addToCart"
                        class="btn-primary"
                    >

                      Add to Cart
                    </button>
                    <button v-else disabled class="btn-primary disabled">

                      Add to Cart
                    </button>

                    <button
                        v-if="selectedArticle && !authStore.shouldUseDAPricing && !dataStore.shouldHideOrderNow"
                        @click="openOrderModal"
                        class="btn-secondary"
                    >
                      Order Now
                    </button>

                    <button class="btn-icon">
                      ❤
                    </button>
                  </div>

                  <div class="product-description">
                    <p>{{ subcategory.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <hr class="section-divider">

            <!-- Product details section -->
            <div class="product-details-section">
              <div class="product-details" v-if="subcategory.howtouse" v-html="subcategory.howtouse"></div>
            </div>
          </div>
        </div>
      </div>

    </div>


    <!-- Order Modal - Redesigned for a modern look -->
    <div v-if="showOrderModal && selectedArticle" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Backdrop -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-black opacity-60"></div>
        </div>

        <!-- Modal container with improved styling -->
        <div
            class="inline-block align-bottom bg-background rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-background-lighter">
          <!-- Modal header with icon -->
          <div class="bg-gradient-to-r from-primary/10 to-background px-6 py-4 border-b border-background-lighter">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-primary/10 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <h3 class="ml-3 text-xl font-semibold text-text">Confirm Your Order</h3>
            </div>
          </div>

          <!-- Product details section -->
          <div class="px-6 py-5">
            <!-- Product name with badge -->
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-lg font-bold text-text">{{ selectedArticle.libelle }}</h4>
              <span class="px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
            {{ formatPrice(selectedArticle.price || selectedArticle.prix) }}
          </span>
            </div>

            <!-- Divider -->
            <div class="border-t border-background-lighter my-4"></div>

            <!-- Order details with improved styling -->
            <div class="space-y-4">
              <!-- Player info section -->
              <div v-if="selectedArticle.message" class="bg-background-light rounded-lg p-4">
                <div class="flex justify-between items-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary mr-2" viewBox="0 0 20 20"
                         fill="currentColor">
                      <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clip-rule="evenodd"/>
                    </svg>
                    <span class="font-medium text-text">{{ selectedArticle.message }}</span>
                  </div>
                </div>
                <p class="mt-2 text-text-secondary bg-background p-2 rounded border border-background-lighter">
                  {{ playerInfo }}</p>
              </div>

              <!-- Quantity section -->
              <div
                  v-if="!selectedArticle.message && !shouldShowAmountField(selectedArticle) && !shouldShowPremiumField(selectedArticle)"
                  class="bg-background-light rounded-lg p-4">
                <div class="flex justify-between items-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary mr-2" viewBox="0 0 20 20"
                         fill="currentColor">
                      <path fill-rule="evenodd"
                            d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                            clip-rule="evenodd"/>
                    </svg>
                    <span class="font-medium text-text">Quantity</span>
                  </div>
                  <span class="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">{{ quantity }}</span>
                </div>
              </div>

              <!-- Games amount section -->
              <div v-if="amount && shouldShowAmountField(selectedArticle)" class="bg-background-light rounded-lg p-4">
                <div class="flex justify-between items-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary mr-2" viewBox="0 0 20 20"
                         fill="currentColor">
                      <path
                          d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                      <path fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                            clip-rule="evenodd"/>
                    </svg>
                    <span class="font-medium text-text">Games Amount (TRY)</span>
                  </div>
                  <span class="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">{{ amount }}</span>
                </div>
                <div class="mt-3 p-3 bg-background rounded border border-background-lighter text-sm">
                  <div v-html="formattedResult"></div>
                </div>
              </div>

              <!-- Premium amount section -->
              <div v-if="amount2 && shouldShowPremiumField(selectedArticle)" class="bg-background-light rounded-lg p-4">
                <div class="flex justify-between items-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary mr-2" viewBox="0 0 20 20"
                         fill="currentColor">
                      <path fill-rule="evenodd"
                            d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05c-.867 0-1.398.468-1.678 1.073-.36.773-.42 1.863-.43 3.326V11c0 1.106.086 1.938.332 2.455.253.533.779.873 1.676.873.487 0 1.022-.115 1.623-.336 1.098-.403 2.415-1.149 4.06-2.224a1 1 0 00.422-1.211 32.875 32.875 0 01-.841-3.037 31.029 31.029 0 01-.748-3.455c-.16-.816-.273-1.635-.273-2.276 0-.6.048-1.04.157-1.386A1 1 0 0010 2c.814 0 1.322.521 1.573 1.052.17.363.245.84.245 1.447 0 .7-.131 1.567-.311 2.52-.097.51-.206 1.043-.331 1.595-.123.555-.258 1.125-.404 1.69a1 1 0 00.421 1.211c1.275.7 2.43 1.305 3.587 1.775.583.237 1.152.42 1.747.515.603.096 1.259.074 1.853-.093a1 1 0 00.734-.839c.037-.265.055-.672.055-1.171 0-1.092-.088-2.572-.495-3.97a1 1 0 00-1.145-.695 2.498 2.498 0 01-1.336-.323c-.322-.186-.577-.452-.77-.778-.27-.454-.425-1.069-.425-1.863 0-.657.131-1.228.365-1.652.063-.114.134-.22.214-.313a1 1 0 00-.076-1.323z"
                            clip-rule="evenodd"/>
                    </svg>
                    <span class="font-medium text-text">Premium Amount (TRY)</span>
                  </div>
                  <span class="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">{{ amount2 }}</span>
                </div>
                <div class="mt-3 p-3 bg-background rounded border border-background-lighter text-sm">
                  <div v-html="formattedResult2"></div>
                </div>
              </div>
            </div>

            <!-- Total price and wallet info -->
            <div class="mt-6 bg-background-light rounded-lg p-4">
              <div class="flex justify-between items-center">
                <span class="font-medium text-text">Total Price:</span>
                <span class="text-lg font-bold text-primary">
                    <span v-if="amount && shouldShowAmountField(selectedArticle)" v-html="formattedResult"></span>
                    <span v-else-if="amount2 && shouldShowPremiumField(selectedArticle)" v-html="formattedResult2"></span>
                    <span v-else>{{ formatPrice((selectedArticle.price || selectedArticle.prix) * quantity) }}</span>
                  </span>
              </div>
              <div v-if="!authStore.shouldHidePrices"
                   class="flex justify-between items-center mt-2 text-sm text-text-secondary">
                <span>Your Wallet:</span>
                <span>{{ authStore.formattedWallet }}</span>
              </div>
            </div>

          </div>

          <!-- Modal footer with improved buttons -->
          <div class="bg-background-light px-6 py-4 flex justify-end space-x-3 border-t border-background-lighter">
            <button
                @click="showOrderModal = false"
                class="px-4 py-2 bg-background hover:bg-background-light text-text border border-background-lighter rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              Cancel
            </button>
            <button
                @click="placeOrder"
                class="px-5 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors duration-200 flex items-center shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Confirm Order
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Add this modal to your template, after your existing modals -->
    <!-- Added to Cart Modal -->
    <div v-if="showAddedToCartModal && addedProduct" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Backdrop -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-black opacity-60"></div>
        </div>

        <!-- Modal container with styling matching your order confirmation modal -->
        <div
            class="inline-block align-bottom bg-background rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full border border-background-lighter">
          <!-- Modal header with success icon -->
          <div class="bg-gradient-to-r from-green-500/10 to-background px-6 py-4 border-b border-background-lighter">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-green-500/10 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 class="ml-3 text-xl font-semibold text-text">Added to Cart</h3>
            </div>
          </div>

          <!-- Product details section -->
          <div class="px-6 py-5">
            <!-- Product info card -->
            <div class="flex items-center bg-background-light p-4 rounded-lg">
              <div class="flex-shrink-0 w-16 h-16 mr-4">
                <img :src="addedProduct.image" :alt="addedProduct.title" class="w-full h-full object-cover rounded-md">
              </div>
              <div class="flex-1 min-w-0">
                <h4 class="text-md font-bold text-text truncate">{{ addedProduct.title }}</h4>
                <div class="flex items-center mt-1">
                  <span class="text-primary font-medium">{{ formatPrice(addedProduct.price) }}</span>
                  <span class="mx-2 text-text-secondary">×</span>
                  <span class="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-sm">{{
                      addedProduct.quantity
                    }}</span>
                </div>
              </div>
            </div>

            <!-- Cart summary -->
            <div class="mt-4 bg-background-light rounded-lg p-4">
              <div class="flex justify-between items-center">
                <span class="text-text-secondary">Items in cart:</span>
                <span class="font-medium text-primary">{{ cartStore.count }}</span>
              </div>
              <div class="flex justify-between items-center mt-2">
                <span class="text-text-secondary">Cart total:</span>
                <span class="font-medium text-primary">{{ formatPrice(cartStore.total) }}</span>
              </div>
            </div>
          </div>

          <!-- Modal footer with action buttons -->
          <div class="bg-background-light px-6 py-4 flex justify-end space-x-3 border-t border-background-lighter">
            <button
                @click="showAddedToCartModal = false"
                class="px-4 py-2 bg-background hover:bg-background-light text-text border border-background-lighter rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
              Continue Shopping
            </button>
            <button
                @click="openCartToggle"
                class="px-5 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors duration-200 flex items-center shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
              View Cart
            </button>
          </div>

          <!-- Progress bar timer -->
          <div class="bg-gray-200 h-1 w-full relative">
            <div
                class="bg-primary h-1 absolute left-0 top-0"
                style="animation: timer-countdown 4s linear forwards;"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Success Modal - Add this to your template -->
    <div v-if="showOrderSuccessModal && orderDetails" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <!-- Backdrop -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-black opacity-60"></div>
        </div>

        <!-- Modal container -->
        <div
            class="inline-block align-bottom bg-background rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full border border-background-lighter">
          <!-- Modal header with success icon -->
          <div class="bg-gradient-to-r from-green-500/10 to-background px-6 py-4 border-b border-background-lighter">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-green-500/10 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 class="ml-3 text-xl font-semibold text-text">Order Placed Successfully!</h3>
            </div>
          </div>

          <!-- Order details section -->
          <div class="px-6 py-5">
            <p class="text-text-secondary mb-4">
              {{ orderDetails.message || 'Your order has been placed and is being processed.' }}</p>

            <!-- Order info card -->
            <div class="bg-background-light rounded-lg p-4 mb-4">
              <div class="flex justify-between items-center mb-2">
                <span class="text-text-secondary">Order ID:</span>
                <span class="font-medium text-text">#{{ orderDetails.order_id || 'N/A' }}</span>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-text-secondary">Article:</span>
                <span class="font-medium text-text">{{ orderDetails.article_name || selectedArticle.libelle }}</span>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-text-secondary">Quantity:</span>
                <span class="font-medium text-text">{{ orderDetails.quantity || quantity }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-text-secondary">Price:</span>
                <span class="font-medium text-primary">{{
                    formatPrice(orderDetails.price)
                  }}</span>
              </div>
            </div>

            <!-- Codes section if available -->
            <div v-if="orderDetails.codes" class="mt-4">
              <div class="flex justify-between items-center mb-2">
                <h4 class="font-medium text-text">Your Codes:</h4>
                <button
                    @click="copyOrderCodes"
                    class="text-primary text-sm flex items-center hover:underline"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                  Copy All
                </button>
              </div>
              <div class="bg-background p-3 rounded border border-background-lighter text-sm overflow-auto max-h-40">
                <pre
                    class="whitespace-pre-wrap break-words text-text-secondary">{{
                    orderDetails.codes.split(' ').join('\n')
                  }}</pre>
              </div>
            </div>

            <!-- Wallet update if available -->
            <div v-if="orderDetails.new_balance !== undefined" class="mt-4 bg-background-light rounded-lg p-4">
              <div class="flex justify-between items-center">
                <span class="text-text-secondary">Updated Wallet Balance:</span>
                <span class="font-medium text-primary">{{ orderDetails.new_balance }}</span>
              </div>
            </div>
          </div>

          <!-- Modal footer -->
          <div class="bg-background-light px-6 py-4 flex justify-end space-x-3 border-t border-background-lighter">
            <button
                @click="showOrderSuccessModal = false"
                class="px-5 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors duration-200 flex items-center shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Got It
            </button>
          </div>

          <!-- Progress bar timer -->
          <div class="bg-gray-200 h-1 w-full relative">
            <div
                class="bg-primary h-1 absolute left-0 top-0"
                style="animation: timer-countdown 8s linear forwards;"
            ></div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useCartStore} from '../stores/cartStore';
import {useDataStore} from '@/stores/dataStore.js'
import {useAuthStore} from '../stores/auth';
import Swal from 'sweetalert2';
import {checkFreeFirePlayer, placeDirectOrder} from "@/services/api.js";
import {slugify} from "@/utils/slugify.js";

export default {
  name: 'SubcategoryDetail',
  props: {
    categorySlug: {
      type: String,
      required: true
    },
    subcategorySlug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(true);
    const error = ref(null);
    const subcategory = ref(null);
    const articles = ref([]);
    const countries = ref([]);
    const selectedCountryId = ref(null);
    const selectedArticle = ref(null);
    const quantity = ref(1);
    const playerInfo = ref('');
    const amount = ref('');
    const amount2 = ref('');
    const showOrderModal = ref(false);
    const imageBaseUrl = import.meta.env.VITE_IMAGE_URL || 'http://127.0.0.1:8000';
    const cartStore = useCartStore();
    const dataStore = useDataStore();
    const authStore = useAuthStore();
    const isProcessingOrder = ref(false);
    const showAddedToCartModal = ref(false);
    const addedProduct = ref(null);
    // Formatted results
    const formattedResult = ref('');
    const formattedResult2 = ref('');
    const showOrderSuccessModal = ref(false);
    const orderDetails = ref(null);
    const codeCopied = ref(false);

    // Validation errors object
    const validationErrors = ref({
      playerInfo: '',
      amount: '',
      amount2: ''
    });

    // Helper methods to determine when to show the amount fields
    const shouldShowAmountField = (article) => {
      if (!article) return false;

      // Check for string '0'
      if (article.prix === '0' || article.price === '0') return true;

      // Check for number 0
      if (article.prix === 0 || article.price === 0) return true;

      // Check for string '0.00' or similar
      if (article.prix === '0.00' || article.price === '0.00') return true;


      return false;
    };

    const shouldShowPremiumField = (article) => {
      if (!article) return false;

      // Check for string '-1'
      if (article.prix === '-1' || article.price === '-1') return true;

      // Check for number -1
      if (article.prix === -1 || article.price === -1) return true;

      return false;
    };

    // Get ID either from props or route
    const id = computed(() => {
      return props.id ? parseInt(props.id) : parseInt(route.params.id);
    });

    // Maximum quantity based on selected article stock
    const maxQuantity = computed(() => {
      if (!selectedArticle.value) return 100;
      const stockCount = getArticleStockCount(selectedArticle.value);
      return stockCount < 1 ? 100 : stockCount;
    });

    // Filter articles based on selected country
    const filteredArticles = computed(() => {
      if (!selectedCountryId.value) return articles.value;

      return articles.value.filter(article => {
        // Match country_id (could be string or number)
        const articleCountryId = article.country_id;
        const selectedId = selectedCountryId.value;

        return (articleCountryId === selectedId.toString() || articleCountryId === selectedId) &&
            article.status !== '-1' && article.status !== '0';
      });
    });

    // Fixed image URL function to handle different image paths
    const getImageUrl = (item, imageField = 'image') => {
      if (!item) return '/placeholder-image.png';

      // Handle different image field names
      if (imageField === 'image_desc' && item.image_desc) {
        // Check for full URL first
        if (item.image_desc.startsWith('http')) return item.image_desc;

        // Check for image_desc_url property
        if (item.image_desc_url) return item.image_desc_url;

        // Construct path based on backend structure
        return `${imageBaseUrl}/assests/Souscategorie/${item.image_desc}`;
      }

      // Main image handling
      // Check for full URL first
      if (item.image && item.image.startsWith('http')) return item.image;

      // Check for image_url property
      if (item.image_url) return item.image_url;

      // Construct path based on backend structure
      return `${imageBaseUrl}/assests/Souscategorie/${item.image}`;
    };


    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };


    // Get article stock count with fallback options
    const getArticleStockCount = (article) => {
      if (!article) return 0;

      // Check different properties depending on API response
      if (article.code_count !== undefined) return article.code_count;
      if (article.stock !== undefined) return article.stock;
      if (article.codes && Array.isArray(article.codes)) {
        return article.codes.filter(code => !code.commande_id).length;
      }

      return 0;
    };

    // Helper function to truncate text
    const truncate = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    // Format price based on user's currency preference and hide_price setting
    const formatPrice = (price) => {


      // If price hiding is enabled, return placeholder
      if (dataStore.shouldHidePrices) {
        return '***';
      }

      // Handle special price values
      if (!price) return 'N/A';
      if (price === '0' || price === '-1' || price === 0 || price === -1) return 'Custom';

      // Get the currency symbol and convert price if needed
      const symbol = dataStore.currencySymbol;
      const convertedPrice = dataStore.convertPrice(price);

      return `${convertedPrice} ${symbol}`;
    };

    // Format individual currency amount
    const formatCurrencyAmount = (amount, currencySymbol) => {
      return `${parseFloat(amount).toFixed(2)} ${currencySymbol}`;
    };

    // Calculate result for custom amount - shows only user's selected currency
    const calculateResult = (inputAmount, getPreferredValue = false, getOriginalPrice = false) => {
      const amount = parseFloat(inputAmount);
      if (isNaN(amount) || amount <= 0) {
        formattedResult.value = '';
        return 0;
      }

      // Get conversion rates from configurations
      const changetry = parseFloat(dataStore.getConfigValue('changetry', 30));
      const changeDa = parseFloat(dataStore.getConfigValue('changeDa', 260));
      const changeegp = parseFloat(dataStore.getConfigValue('changeegp', 50));
      const changeeuro = parseFloat(dataStore.getConfigValue('changeeuro', 0.88));

      // Calculate conversions
      const usdValue = amount / changetry;
      const daValue = usdValue * changeDa;
      const egpValue = usdValue * changeegp;
      const euroValue = usdValue * changeeuro;

      if(getOriginalPrice) return  usdValue;
      // Get user's preferred currency value and symbol
      const preferredValue = getPreferredCurrencyValue(usdValue, daValue, euroValue, egpValue);
      if(getPreferredValue) return preferredValue;
      const currencySymbol = dataStore.currencySymbol;

      // Show only user's preferred currency
      formattedResult.value = `
    <div class="font-medium text-text">Converted amount: ${formatCurrencyAmount(preferredValue, currencySymbol)}</div>
  `;

      // Return the preferred currency value for calculations
      return preferredValue;
    };

// Update calculateResult2 with the same pattern
    const calculateResult2 = (inputAmount, getPreferredValue = false, getOriginalPrice = false) => {
      const amount = parseFloat(inputAmount);
      if (isNaN(amount) || amount <= 0) {
        formattedResult2.value = '';
        return 0;
      }

      // Get conversion rates from configurations
      const changetry2 = parseFloat(dataStore.getConfigValue('changetry2', 35));
      const changeDa = parseFloat(dataStore.getConfigValue('changeDa', 260));
      const changeegp = parseFloat(dataStore.getConfigValue('changeegp', 50));
      const changeeuro = parseFloat(dataStore.getConfigValue('changeeuro', 0.88));

      // Calculate conversions
      const usdValue = amount / changetry2;
      const daValue = usdValue * changeDa;
      const egpValue = usdValue * changeegp;
      const euroValue = usdValue * changeeuro;


      if(getOriginalPrice) return  usdValue;
      // Get user's preferred currency value and symbol
      const preferredValue = getPreferredCurrencyValue(usdValue, daValue, euroValue, egpValue);
      if(getPreferredValue) return preferredValue;
      const currencySymbol = dataStore.currencySymbol;

      // Show only user's preferred currency
      formattedResult2.value = `
    <div class="font-medium text-text">Converted amount: ${formatCurrencyAmount(preferredValue, currencySymbol)}</div>
  `;

      // Return the preferred currency value for calculations
      return preferredValue;
    };

    // Get preferred currency value based on user setting
    const getPreferredCurrencyValue = (usdValue, daValue, euroValue, egpValue) => {
      switch (parseInt(dataStore.user.is_da)) {
        case 1:
          return euroValue; // Euro
        case 2:
        case -1:
          return daValue; // Algerian Dinar
        case 3:
          return egpValue; // Egyptian Pound
        case 0:
          return usdValue; // Egyptian Pound
        default:
          return usdValue; // USD (default)
      }
    };

    // Quantity controls
    const incrementQuantity = () => {
      if (quantity.value < maxQuantity.value) {
        quantity.value++;
      }
    };

    const decrementQuantity = () => {
      if (quantity.value > 1) {
        quantity.value--;
      }
    };

    // Select country and filter articles
    const selectCountry = (countryId) => {
      selectedCountryId.value = countryId;

      // If we have filtered articles, select the first one
      if (filteredArticles.value.length > 0) {
        selectArticle(filteredArticles.value[0]);
      } else {
        selectedArticle.value = null;
      }
    };

    // Select article and update price display
    const selectArticle = (article) => {

      selectedArticle.value = article;

      // Also update selected country to match the article's country
      if (article.country_id) {
        selectedCountryId.value = typeof article.country_id === 'string'
            ? parseInt(article.country_id)
            : article.country_id;
      }

      // Reset inputs when article changes
      quantity.value = 1;
      playerInfo.value = '';
      amount.value = '';
      amount2.value = '';
      formattedResult.value = '';
      formattedResult2.value = '';

      // Reset player info and check result when selecting new article
      playerInfo.value = '';
      playerCheckResult.value = null;
      validationErrors.value.playerInfo = '';

      clearPlayerCheckTimeout();

      // Clear validation errors
      clearValidationErrors();

    };

    // Clear validation errors
    const clearValidationErrors = () => {
      validationErrors.value = {
        playerInfo: '',
        amount: '',
        amount2: ''
      };
    };

    // Validate inputs based on article requirements
    const validateInputs = () => {
      let isValid = true;
      clearValidationErrors();

      if (!selectedArticle.value) return false;

      // Check for message requirement
      if (selectedArticle.value.message) {
        if (!playerInfo.value.trim()) {
          validationErrors.value.playerInfo = 'Please provide the required information.';
          isValid = false;
        }
      }
      if (requiresPlayerCheck.value) {
        if (!playerCheckResult.value || !playerCheckResult.value.success) {
          validationErrors.value.playerInfo = 'Please check and validate the player ID first';

          Swal.fire({
            icon: 'warning',
            title: 'Player Not Validated',
            text: 'Please click "Check Player" button to validate the player ID before ordering.',
            confirmButtonColor: '#f59e0b',
            background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
            color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()
          });
          return;
        }else if(!isPlayerCheckValid()){
          validationErrors.value.playerInfo = 'Player verification expired. Please check again.';
          Swal.fire({
            icon: 'warning',
            title: 'Player Not Validated',
            text: 'Player ID verification expired or not validated. Please click "Check Player" button.',
            confirmButtonColor: '#f59e0b',
            background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
            color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim(),
          });
          return;
        }
      }
      // Check for amount requirement
      if (shouldShowAmountField(selectedArticle.value)) {
        if (!amount.value || parseFloat(amount.value) <= 0) {
          validationErrors.value.amount = 'Please enter a valid amount.';
          isValid = false;
        }
      }

      // Check for premium amount requirement
      if (shouldShowPremiumField(selectedArticle.value)) {
        if (!amount2.value || parseFloat(amount2.value) <= 0) {
          validationErrors.value.amount2 = 'Please enter a valid premium amount.';
          isValid = false;
        }
      }

      return isValid;
    };
    const getArticleImage = (article) => {
      if (!article) return '/placeholder-image.png';

      // Try article image first
      if (article.image) {
        if (article.image.startsWith('http')) return article.image;
        if (article.image_url) return article.image_url;
        return `${imageBaseUrl}/assests/Article/${article.image}`;
      }

      // Fallback to subcategory image
      return getImageUrl(subcategory.value);
    };


    // Open order modal after validation
    const openOrderModal = () => {
      if (validateInputs()) {
        // Calculate results if needed
        if (shouldShowAmountField(selectedArticle.value)) {
          calculateResult(amount.value);
        }
        if (shouldShowPremiumField(selectedArticle.value)) {
          calculateResult2(amount2.value);
        }

        showOrderModal.value = true;
      }
    };

    // Add to cart function with validation
    const addToCart = () => {
      if (!validateInputs()) return;

      // Calculate price based on amount if necessary
      let calculatedPrice = null;
      let originalPrice = null;
      if (shouldShowAmountField(selectedArticle.value)) {

        calculatedPrice = calculateResult(amount.value, true);
        originalPrice = calculateResult(amount.value, false, true);
     } else if (shouldShowPremiumField(selectedArticle.value)) {
        calculatedPrice = calculateResult2(amount2.value, true);
        originalPrice = calculateResult2(amount2.value, false, true);

      }

      // Create a proper product object from the selected article
      const product = {
        id: selectedArticle.value.id,
        title: selectedArticle.value.libelle || (subcategory.value ? subcategory.value.libelle : 'Product'),
        description: selectedArticle.value.description || (subcategory.value ? subcategory.value.description : ''),
        price: originalPrice || parseFloat(selectedArticle.value.price || selectedArticle.value.prix || 0),
        originalPrice: originalPrice || parseFloat(selectedArticle.value.price || selectedArticle.value.prix || 0),
        image: getArticleImage(selectedArticle.value),
        quantity: quantity.value,
        playerInfo: playerInfo.value || null,
        amount: amount.value || null,
        amount2: amount2.value || null,
        message: selectedArticle.value.message || null,
        is_custom_price: shouldShowAmountField(selectedArticle.value) || shouldShowPremiumField(selectedArticle.value)
      };


      // Add the item to the cart store
      cartStore.addItem(product);

      // Show a temporary notification
      // Show custom modal for added to cart
      addedProduct.value = product;
      showAddedToCartModal.value = true;

      setTimeout(() => {
        showAddedToCartModal.value = false;
      }, 4000);
    };
    const openCartToggle = () => {
      cartStore.toggleCart();
    };
    // Place order function now uses the same validation
    const placeOrder = async () => {
      // We already validated before showing the modal, but just to be safe
      if (!validateInputs()) {
        showOrderModal.value = false;
        return;
      }

      // Check if user is logged in
      if (!authStore.isLoggedIn) {
        showOrderModal.value = false;
        Swal.fire({
          title: 'Login Required',
          text: 'Please login to place an order',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Login Now',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            router.push('/login');
          }
        });
        return;
      }

      // Calculate price based on amount if necessary
      let calculatedPrice = null;
      if (shouldShowAmountField(selectedArticle.value)) {
        calculatedPrice = calculateResult(amount.value);
      } else if (shouldShowPremiumField(selectedArticle.value)) {
        calculatedPrice = calculateResult2(amount2.value);
      }


      // Check if user has enough wallet balance
      const articlePrice = calculatedPrice ?? parseFloat(selectedArticle.value.price || selectedArticle.value.prix || 0);
      const totalPrice = articlePrice * quantity.value;

      if (Number(authStore.userWallet) < (calculatedPrice ? Number(totalPrice) : Number(dataStore.convertPrice(totalPrice)))) {

       showOrderModal.value = false;
        Swal.fire({
          title: 'Insufficient Balance',
          text: 'You do not have enough funds in your wallet. Would you like to add funds?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Add Funds',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            router.push('/panel/charge-wallet');
          }
        });
        return;
      }

      // Show loading indicator
      showOrderModal.value = false;
      Swal.fire({
        title: 'Processing Order',
        text: 'Please wait while we process your order...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // Prepare order data
        const orderData = {
          article_id: selectedArticle.value.id,
          quantity: quantity.value,
          playerinfo: playerInfo.value || null,
          amount: amount.value || null,
          amount2: amount2.value || null
        };


        // Call the API to place the order
        const response = await placeDirectOrder(orderData);

        // Handle success


        // Update wallet balance if returned in response
        if (response.new_balance !== undefined) {
          // Update the wallet balance in authStore
          if (dataStore.user) {
            dataStore.updateWalletBalance(response.new_balance);
            authStore.updateWalletBalance(response.new_balance);

          }
        }
        Swal.close();
        orderDetails.value = {
          order_id: response.order_id,
          article_name: selectedArticle.value.libelle,
          quantity: quantity.value,
          price: response.price?? totalPrice,
          message: response.message,
          codes: response.codes,
          new_balance: response.new_balance
        };

        showOrderSuccessModal.value = true;

        // Auto-close after 8 seconds
        setTimeout(() => {
          showOrderSuccessModal.value = false;
        }, 8000);

      } catch (error) {
        console.error('Order failed:', error);

        // Handle error
        Swal.fire({
          title: 'Order Failed',
          text: error.message || 'There was a problem placing your order. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
      clearPlayerCheckTimeout();

    };
    // Extract countries from subcategory data or articles
    const extractCountries = (subcategoryData) => {
      if (!subcategoryData) return [];

      // Try different property names based on API response format
      if (subcategoryData.countries && Array.isArray(subcategoryData.countries)) {
        return subcategoryData.countries;
      }

      // If countries aren't directly available, extract from articles
      const uniqueCountries = new Map();

      articles.value.forEach(article => {
        // Skip articles with status -1 or 0
        if (article.status === '-1' || article.status === '0') return;

        // If article has country object, use it
        if (article.country && article.country.id) {
          if (!uniqueCountries.has(article.country.id)) {
            uniqueCountries.set(article.country.id, article.country);
          }
          return;
        }

        // Fallback for articles without country object but with country_id
        const countryId = article.country_id;
        if (countryId && !uniqueCountries.has(countryId)) {
          uniqueCountries.set(countryId, {
            id: countryId,
            name: `Country ${countryId}`
          });
        }
      });

      return Array.from(uniqueCountries.values());
    };




// Main function to load subcategory data - REPLACE YOUR EXISTING loadData
    const loadData = async () => {
      if (!dataStore.isDataReady) {
        await dataStore.fetchData()
      }

      loading.value = true;
      error.value = null;

      try {
        // Get subcategory by slug path
        subcategory.value = dataStore.getSubcategoryBySlugPath(
            props.categorySlug,
            props.subcategorySlug
        );

        if (!subcategory.value) {
          error.value = 'Product not found';
          await router.push('/404');
          return;
        }

        // Load articles for this subcategory
        await loadArticles();

        // Check if there's an article query parameter
        const articleParam = route.query.article;
        if (articleParam && filteredArticles.value.length > 0) {
          // Find article by slug
          const articleSlug = slugify(articleParam);
          const matchedArticle = filteredArticles.value.find(article =>
              slugify(article.libelle) === articleSlug
          );

          if (matchedArticle) {
            selectArticle(matchedArticle);
          } else {
          }
        }

      } catch (err) {
        error.value = "Failed to load product data. Please try again.";
      } finally {
        loading.value = false;
      }
    };

// Update loadArticles to use subcategory.value.id instead of id.value
    const loadArticles = async () => {
      if (!subcategory.value) return;

      try {
        articles.value = dataStore.getArticlesBySubcategoryId(subcategory.value.id);

        // Extract countries after loading articles
        countries.value = extractCountries(subcategory.value);

        // Set default country if any
        if (countries.value.length > 0) {
          selectCountry(countries.value[0].id);
        } else if (articles.value.length > 0) {
          // If no countries, just select the first article
          selectArticle(articles.value[0]);
        }
      } catch (err) {
        console.error('Failed to load articles:', err);
        articles.value = [];
      }
    };

// Watch for article query param changes
    watch(() => route.query.article, (newArticle) => {
      if (newArticle && filteredArticles.value.length > 0) {
        const articleSlug = slugify(newArticle);
        const matchedArticle = filteredArticles.value.find(article =>
            slugify(article.libelle) === articleSlug
        );

        if (matchedArticle) {
          selectArticle(matchedArticle);
        }
      }
    }, { immediate: false });

// Watch for route param changes (if user navigates between subcategories)
    watch(() => [props.categorySlug, props.subcategorySlug], () => {
      loadData();
    }, { immediate: false });



    const copyOrderCodes = () => {
      if (!orderDetails.value || !orderDetails.value.codes) return;

      navigator.clipboard.writeText(orderDetails.value.codes)
          .then(() => {
            // Show a small toast notification
            const toast = document.createElement('div');
            toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
            toast.textContent = 'Codes copied to clipboard!';
            document.body.appendChild(toast);

            setTimeout(() => {
              document.body.removeChild(toast);
            }, 2000);
          })
          .catch(err => {
            console.error('Failed to copy codes:', err);
          });
    };

    const getArticleBadge = (article) => {

      const checkBoolean = (value) => {
        if (value === true || value === 1 || value === '1' || value === 'true') {
          return true;
        }
        return false;
      };

      if (checkBoolean(article.is_limited_offer)) {
        return 'Limited offer';
      }
      if (checkBoolean(article.is_best_seller)) {
        return 'Best Seller';
      }
      if (checkBoolean(article.is_new_arrival)) {
        return 'New Arrival';
      }
      return null;

    };

    const getBadgeClass = (article) => {
      const badge = getArticleBadge(article);
      switch (badge) {
        case 'Limited offer':
          return 'badge-limited';
        case 'Best Seller':
          return 'badge-bestseller';
        case 'New Arrival':
          return 'badge-new';
        default:
          return '';
      }
    };
    const getBadgeIcon = (article) => {
      const badge = getArticleBadge(article);
      switch (badge) {
        case 'Limited Offer':
          return '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"/></svg>';
        case 'Best Seller':
          return '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.66 11.2C17.43 10.9 17.15 10.64 16.89 10.38C16.22 9.78 15.46 9.35 14.82 8.72C13.33 7.26 13 4.85 13.95 3C13.74 3.09 13.54 3.22 13.34 3.36C11.66 4.41 11.26 6.96 12.25 8.87C11.25 8.87 10.35 9.29 9.71 9.94C8.07 11.58 8.07 14.27 9.71 15.91C11.35 17.55 14.04 17.55 15.68 15.91C16.32 15.27 16.74 14.37 16.74 13.37C18.65 14.36 21.2 13.96 22.25 12.28C22.39 12.08 22.52 11.88 22.61 11.67C20.76 12.62 18.35 12.29 17.66 11.2Z"/></svg>';
        case 'New Arrival':
          return '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4V12L16,16L17.4,14.6L14,11.2V4H12Z"/></svg>';
        default:
          return '';
      }
    };

    const isArticleDisabled = (article) => {
      // Get stock count

      console.log('Checking if article is disabled:', article);
      console.log('Article offer_id:', article.offer_id,article.libelle);
      console.log('Data store shouldHideCart:', dataStore.shouldHideCart)

      if(article.offer_id !== null && article.offer_id !== ''){
        if(!dataStore.shouldHideCart) return  true;
      }
      const stockCount = getArticleStockCount(article);
      return (stockCount === 0 && !shouldShowAmountField(article) && !shouldShowPremiumField(article) && !article.message && article.message !== '' && article.offer_id === null);

      // // Check if article type is -1 or 0 (these types ignore stock)
      // const isSpecialType = article.prix === -1 || article.prix === 0 ||
      //     article.price === -1 || article.price === 0 ||
      //     article.prix === '-1' || article.prix === '0' ||
      //     article.price === '-1' || article.price === '0';

      // Disable if stock is 0 AND type is NOT special (-1 or 0)
    };

    const disabledArticlesMap = computed(() => {
        const map = {};
        if (filteredArticles.value) {
            filteredArticles.value.forEach(article => {
                map[article.id] = isArticleDisabled(article);
            });
        }
        return map;
    });

    // FreeFire player check states
    const isCheckingPlayer = ref(false); // Loading state when checking player
    const playerCheckResult = ref(null); // Store the check result

    const playerCheckTimestamp = ref(null);
    const playerCheckTimer = ref(null);
    const PLAYER_CHECK_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

    // Start 10-minute timeout after player check
    const startPlayerCheckTimeout = () => {
      // Clear any existing timer
      clearPlayerCheckTimeout();

      // Set new timer for 10 minutes
      playerCheckTimer.value = setTimeout(() => {
        // Expire the check
        playerCheckResult.value = null;
        playerInfo.value = '';
        validationErrors.value.playerInfo = 'Player verification expired after 10 minutes. Please check again.';
        playerCheckTimestamp.value = null;

        // Show expiry notification
        Swal.fire({
          icon: 'warning',
          title: 'Verification Expired',
          text: 'Player ID verification has expired after 10 minutes. Please check the player again.',
          confirmButtonColor: '#f59e0b',
          background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
          color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim(),
        });
      }, PLAYER_CHECK_TIMEOUT);
    };

// Clear the timeout
    const clearPlayerCheckTimeout = () => {
      if (playerCheckTimer.value) {
        clearTimeout(playerCheckTimer.value);
        playerCheckTimer.value = null;
      }
    };

// Check if player check is still valid
    const isPlayerCheckValid = () => {
      if (!playerCheckResult.value || !playerCheckResult.value.success || !playerCheckTimestamp.value) {
        return false;
      }

      const elapsed = Date.now() - playerCheckTimestamp.value;
      return elapsed < PLAYER_CHECK_TIMEOUT;
    };


    const requiresPlayerCheck = ref(false);

    watch(selectedArticle, (newArticle) => {
      if (!newArticle) {
        requiresPlayerCheck.value = false;
      } else {
        requiresPlayerCheck.value = newArticle.offer_id != null && newArticle.offer_id !== '';
      }
    }, { immediate: true });

    /**
     * Check FreeFire Player ID via API
     * Called when user clicks "Check Player" button
     */
    const checkPlayer = async () => {
      // Clear any previous validation errors
      validationErrors.value.playerInfo = '';
      playerCheckResult.value = null;

      // Validate player ID is not empty
      if (!playerInfo.value || playerInfo.value.trim() === '') {
        validationErrors.value.playerInfo = 'Please enter a Player ID';
        return;
      }

      // Set loading state
      isCheckingPlayer.value = true;

      try {
        // Call the API function
        const result = await checkFreeFirePlayer(playerInfo.value.trim());

        // Handle the response based on backend structure
        if (result.success === true && result.player.success === true) {
          // Check for duplicate task
         // {"success":true,"player":{"success":false,"msg":"WRONG_ID"}}
          if (result.player.msg === 'DUPLICATE_TASK') {
            playerCheckResult.value = {
              success: false,
              message: 'This player has already been checked recently. Please try again later.',
              type: 'duplicate'
            };

            // Show warning alert
            await Swal.fire({
              icon: 'warning',
              title: 'Player Already Checked',
              text: 'This player ID has already been validated recently. Please try again later.',
              confirmButtonColor: '#f59e0b',
              background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()
            });
          } else {
            // Success - player validated
            playerCheckResult.value = {
              success: true,
              player_name: result.player_name || result.player?.player_name || 'Unknown',
              region: result.region || result.player?.region || 'Unknown',
              type: 'success'
            };
            playerCheckTimestamp.value = Date.now();
            startPlayerCheckTimeout();
            // Show success message
            await Swal.fire({
              icon: 'success',
              title: 'Player Validated Successfully!',
              html: `
    <div style="
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%);
      border: 2px solid rgba(16, 185, 129, 0.3);
      border-radius: 12px;
      padding: 20px;
      margin-top: 20px;
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.2);
    ">
      <!-- Player Icon -->
      <div style="
        width: 60px;
        height: 60px;
        margin: 0 auto 15px;
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="white" viewBox="0 0 16 16">
          <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
          <path fill-rule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"/>
        </svg>
      </div>

      <!-- Player Info Cards -->
      <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
        <!-- Player Name Card -->
        <div style="
          flex: 1;
          min-width: 200px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 8px;
          padding: 15px;
          text-align: center;
          transition: transform 0.2s;
        ">
          <div style="
            font-size: 12px;
            color: #10b981;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          ">
            Player Name
          </div>
          <div style="
            font-size: 18px;
            font-weight: bold;
            color: ${getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()};
            word-break: break-word;
          ">
            ${playerCheckResult.value.player_name}
          </div>
        </div>

        <!-- Region Card -->
        <div style="
          flex: 1;
          min-width: 200px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 8px;
          padding: 15px;
          text-align: center;
          transition: transform 0.2s;
        ">
          <div style="
            font-size: 12px;
            color: #10b981;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          ">
            Region
          </div>
          <div style="
            font-size: 18px;
            font-weight: bold;
            color: ${getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()};
          ">
            <span style="
              display: inline-block;
              padding: 6px 16px;
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
              color: white;
              border-radius: 20px;
              font-size: 14px;
              font-weight: 600;
              letter-spacing: 1px;
              box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
            ">
              ${playerCheckResult.value.region}
            </span>
          </div>
        </div>
      </div>

      <!-- Success Message -->
      <div style="
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid rgba(16, 185, 129, 0.2);
        text-align: center;
      ">
        <p style="
          margin: 0;
          font-size: 14px;
          color: ${getComputedStyle(document.documentElement).getPropertyValue('--color-text-secondary').trim()};
          font-style: italic;
        ">
          ✓ You can now proceed with your order
        </p>
      </div>
    </div>
  `,
              confirmButtonText: 'Proceed to Order',
              confirmButtonColor: '#10b981',
              buttonsStyling: true,
              customClass: {
                popup: 'player-validated-popup',
                confirmButton: 'player-validated-button'
              },
              background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim(),
              showClass: {
                popup: 'animate__animated animate__fadeInDown animate__faster'
              },
              hideClass: {
                popup: 'animate__animated animate__fadeOutUp animate__faster'
              }
            });

          }
        } else if (result.success === true && (result.player.success === false  ||result.player.msg === 'WRONG_ID' )) {
          // Handle wrong player ID
          if (result.player.msg === 'WRONG_ID' || result.message?.includes('Invalid')) {
            playerCheckResult.value = {
              success: false,
              message: 'Invalid Player ID. Please check and try again.',
              type: 'error'
            };

            validationErrors.value.playerInfo = 'Invalid Player ID';

            // Show error alert
            await Swal.fire({
              icon: 'error',
              title: 'Invalid Player ID',
              text: 'The Player ID you entered is incorrect. Please verify and try again.',
              confirmButtonColor: '#ef4444',
              background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()
            });
          } else {
            // Other error
            playerCheckResult.value = {
              success: false,
              message: result.message || 'Unable to validate player. Please try again.',
              type: 'error'
            };

            validationErrors.value.playerInfo = result.message || 'Validation failed';
          }
        }

      } catch (error) {
        console.error('Player check error:', error);

        playerCheckResult.value = {
          success: false,
          message: 'Unable to check player. Please try again later.',
          type: 'error'
        };

        validationErrors.value.playerInfo = 'Connection error. Please try again.';

        // Show generic error alert
        await Swal.fire({
          icon: 'error',
          title: 'Connection Error',
          text: 'Unable to validate player ID. Please check your connection and try again.',
          confirmButtonColor: '#ef4444',
          background: getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim(),
          color: getComputedStyle(document.documentElement).getPropertyValue('--color-text').trim()
        });

      } finally {


        // Always remove loading state
        isCheckingPlayer.value = false;
      }
    };

    /**
     * Reset player check result when player info changes
     * Allows user to check another player ID
     */
    const resetPlayerCheck = () => {
      if (playerCheckResult.value !== null) {
        playerCheckResult.value = null;
      }
      if (validationErrors.value.playerInfo !== '') {
        validationErrors.value.playerInfo = '';
      }
      clearPlayerCheckTimeout();
    };


    onMounted(() => {
      if (dataStore.isDataReady) {
        // Data already loaded, proceed immediately
        loadData()
      } else {
        // Wait for data to be ready
      }
      loadData();
    });
    onUnmounted(() => {
      clearPlayerCheckTimeout();
    });


    return {
      id,
      loading,
      error,
      subcategory,
      articles,
      countries,
      selectedCountryId,
      selectedArticle,
      filteredArticles,
      quantity,
      playerInfo,
      amount,
      amount2,
      showOrderModal,
      maxQuantity,
      validationErrors,
      formattedResult,
      formattedResult2,
      cartStore,
      truncate,
      formatPrice,
      getImageUrl,
      handleImageError,
      getArticleStockCount,
      // isArticleDisabled,
      calculateResult,
      calculateResult2,
      incrementQuantity,
      decrementQuantity,
      selectCountry,
      selectArticle,
      clearValidationErrors,
      validateInputs,
      openOrderModal,
      addToCart,
      placeOrder,
      loadData,
      authStore,
      dataStore,
      shouldShowAmountField,
      shouldShowPremiumField,
      getPreferredCurrencyValue,
      isProcessingOrder,
      showAddedToCartModal,
      addedProduct,
      openCartToggle,
      showOrderSuccessModal,
      orderDetails,
      copyOrderCodes,
      getArticleBadge,
      getBadgeClass,
      getBadgeIcon,
      isArticleDisabled,
      disabledArticlesMap,
      checkPlayer,                // Add this method
      resetPlayerCheck,
      isCheckingPlayer,           // Add this
      playerCheckResult,          // Add this
      requiresPlayerCheck,
      playerCheckTimestamp,
      isPlayerCheckValid,
      clearPlayerCheckTimeout,
      startPlayerCheckTimeout
    };
  }
}
</script>

<style scoped>
/* Import Google Fonts for professional typography */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600;700&display=swap');


/* SweetAlert Player Validation Custom Styles */
.player-validated-popup {
  border-radius: 16px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
}

.player-validated-button {
  font-weight: 600 !important;
  padding: 12px 32px !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

.player-validated-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.5) !important;
}

/* Optional: Add animate.css for animations (if not already included) */
/* Include this in your index.html or main.js:
   <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css"/>
*/

/* Check Player Button */
.btn-check-player {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-width: 140px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.btn-check-player:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-check-player:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-check-player.loading {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
}

/* Input success state */
.text-input.success {
  border-color: #10b981;
  background-color: rgba(16, 185, 129, 0.05);
}

.text-input.success:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

/* Player Check Result Container */
.player-check-result {
  margin-top: 0.75rem;
  padding: 1rem;
  border-radius: 0.5rem;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Success Result */
.result-success {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  padding: 0.75rem;
  border-radius: 0.5rem;
}

.result-success .result-content {
  flex: 1;
}

.result-success .result-title {
  font-weight: 600;
  color: #10b981;
  margin-bottom: 0.5rem;
}

.result-success .player-details {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

/* Error Result */
.result-error {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 0.75rem;
  border-radius: 0.5rem;
}

.result-error .result-content {
  flex: 1;
}

.result-error .result-title {
  font-weight: 600;
  color: #ef4444;
}

/* Flex utility for input + button layout */
.flex {
  display: flex;
}

.gap-2 {
  gap: 0.5rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-4 {
  margin-left: 1rem;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .btn-check-player {
    min-width: 120px;
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }

  .player-details {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .player-details .ml-4 {
    margin-left: 0;
  }
}

/* Main product layout - 70% smaller image */
.product-layout {
  display: flex;
  gap: 2rem;
  min-height: 360px; /* Reduced from 600px */
}

.product-image-section {
  flex: 0.85; /* Reduced from 1.2 */
  max-width: 50%; /* Reduced from 65% */
}

.product-info-section {
  flex: 1.15; /* Increased from 0.8 */
  max-width: 50%; /* Increased from 35% */
  padding-left: 1rem;
}

/* Smaller image styling */
.image-container {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: var(--color-background-light);
}

.main-product-image {
  width: 100%;
  height: 360px; /* Reduced from 600px (70% smaller) */
  object-fit: cover;
  border-radius: 16px;
}

/* Product info styling */
.product-title {
  font-family: 'Inter', sans-serif;
  font-size: 2rem;
  font-weight: 800;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.product-rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.stars {
  display: flex;
  color: #f59e0b;
}

.star {
  font-size: 1rem;
}

.rating-text {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
}

.price-display {
  margin-bottom: 1.5rem;
}

.current-price {
  font-family: 'JetBrains Mono', monospace;
  font-size: 2rem;
  font-weight: 700;
  color: #6366f1;
}

.old-price {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.25rem;
  color: var(--color-text-secondary);
  text-decoration: line-through;
  margin-left: 0.5rem;
}

/* Section titles */
.section-title {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
  display: block;
}

/* Country selection */
.country-selection {
  margin-bottom: 1.5rem;
}

.country-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.country-input {
  display: none;
}

.country-label {
  padding: 0.5rem 1rem;
  border-radius: 50px;
  background: var(--color-background-light);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
}

.country-label:hover,
.country-label.selected {
  background: #6366f1;
  color: white;
}

/* Articles grid with professional badges */
.article-selection {
  margin-bottom: 1.5rem;
}

.articles-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.article-card {
  position: relative;
  border-radius: 12px;
  overflow: visible; /* Changed to show badges properly */
}

/* Professional Article badges with icons */
.article-badge {
  position: absolute;
  top: -6px;
  left: -6px;
  z-index: 15;
  border-radius: 8px;
  padding: 0;
  font-family: 'Inter', sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: rotate(-2deg);
  transition: all 0.3s ease;
}

.article-badge:hover {
  transform: rotate(-2deg) scale(1.05);
}

.badge-content {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
}

.badge-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
}

.badge-icon {
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-icon svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.badge-text {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

/* Badge color schemes */
.badge-limited {
  background: linear-gradient(135deg, #ec4899 0%, #f97316 100%);
  color: white;
}

.badge-limited .badge-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #ec4899 0%, #f97316 100%);
  opacity: 0.9;
  z-index: -1;
}

.badge-bestseller {
  background: linear-gradient(135deg, #f59e0b 0%, #dc2626 100%);
  color: white;
}

.badge-bestseller .badge-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f59e0b 0%, #dc2626 100%);
  opacity: 0.9;
  z-index: -1;
}

.badge-new {
  background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
  color: white;
}

.badge-new .badge-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
  opacity: 0.9;
  z-index: -1;
}

.article-input {
  display: none;
}

.article-label {
  display: block;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.article-label::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, rgba(139, 92, 246, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.article-label:hover::before {
  opacity: 1;
}

.article-label:hover {
  border-color: #c3c4f7;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.article-label.selected {
  border-color: #6366f1;
  background: #f0f9ff;
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.2);
}

.article-label.selected::before {
  opacity: 1;
}

.article-icon {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 10px;
  flex-shrink: 0;
  background: var(--color-background-light);
}

.article-text {
  flex: 1;
  min-width: 0;
}

/* Dark mode support */
.dark .article-icon {
  background: #374151;
}

/* Mobile responsive - even smaller on mobile */
@media (max-width: 640px) {
  .article-icon {
    width: 64px;
    height: 64px;
    margin-right: 10px;
  }
}

.article-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.article-name {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  color: var(--color-text);
  font-size: 0.875rem;
  line-height: 1.3;
}

.price-section {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.article-price {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 800;
  color: #6366f1;
  font-size: 1rem;
  text-shadow: 0 1px 2px rgba(99, 102, 241, 0.1);
}

.article-old-price {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  text-decoration: line-through;
}

/* Form inputs */
.input-section {
  margin-bottom: 1.5rem;
}

.text-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.text-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.text-input.error {
  border-color: #ef4444;
}

.error-text {
  color: #ef4444;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Quantity controls */
.quantity-section {
  margin-bottom: 1.5rem;
}

.quantity-controls {
  display: flex;
  align-items: center;
  width: fit-content;
}

.quantity-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #e5e7eb;
  background: var(--color-background-light);
  color: var(--color-text);
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  transition: all 0.2s ease;
}

.quantity-btn:hover:not(:disabled) {
  border-color: #6366f1;
  background: #f0f9ff;
}

.quantity-btn:first-child {
  border-radius: 8px 0 0 8px;
}

.quantity-btn:last-child {
  border-radius: 0 8px 8px 0;
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-input {
  width: 60px;
  height: 40px;
  border: 2px solid #e5e7eb;
  border-left: none;
  border-right: none;
  text-align: center;
  background: var(--color-background);
  color: var(--color-text);
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
}

.btn-primary.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: 2px solid #6366f1;
  background: transparent;
  color: #6366f1;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover {
  background: #6366f1;
  color: white;
}

.btn-icon {
  width: 48px;
  height: 48px;
  border: 2px solid #e5e7eb;
  background: var(--color-background-light);
  color: #ef4444;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.25rem;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  border-color: #ef4444;
  background: #fef2f2;
}

.btn-icon-svg {
  width: 20px;
  height: 20px;
}

.result-display {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: var(--color-background-lighter);
  border-radius: 8px;
  color: var(--color-text);
  font-family: 'Inter', sans-serif;
}

.product-description {
  margin-top: 1.5rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  font-family: 'Inter', sans-serif;
}

.section-divider {
  border: none;
  height: 1px;
  background: var(--color-background-lighter);
  margin: 2rem 0;
}

/* Dark mode support */
.dark .article-label,
[data-theme="dark"] .article-label {
  background: #1f2937;
  border-color: #374151;
}

.dark .article-label:hover,
[data-theme="dark"] .article-label:hover {
  border-color: #6b7280;
  background: #111827;
}

.dark .article-label.selected,
[data-theme="dark"] .article-label.selected {
  border-color: #6366f1;
  background: #1e1b4b;
}

.dark .text-input,
[data-theme="dark"] .text-input {
  background: #1f2937;
  border-color: #374151;
}

.dark .quantity-btn,
[data-theme="dark"] .quantity-btn {
  background: #1f2937;
  border-color: #374151;
}

.dark .quantity-input,
[data-theme="dark"] .quantity-input {
  background: #1f2937;
  border-color: #374151;
}

/* Responsive design */
@media (max-width: 1024px) {
  .product-layout {
    flex-direction: column;
  }

  .product-image-section,
  .product-info-section {
    max-width: 100%;
  }

  .main-product-image {
    height: 280px; /* Adjusted for mobile */
  }
}

@media (max-width: 640px) {
  .articles-grid {
    grid-template-columns: 1fr;
  }

  .main-product-image {
    height: 200px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .badge-text {
    font-size: 0.65rem;
  }

  .badge-icon {
    width: 12px;
    height: 12px;
  }
}


/* Add these styles to your <style scoped> section */

.out-of-stock-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 20;
  background: rgba(239, 68, 68, 0.95);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
  white-space: nowrap;
}

/* Adjust disabled article opacity to show content better */
.article-card.article-disabled {
  opacity: 0.75; /* Changed from 0.6 to show content better */
  position: relative;
}

.article-card.article-disabled::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.4);
  pointer-events: none;
  border-radius: 12px;
  z-index: 15;
}

.dark .article-card.article-disabled::after {
  background: rgba(0, 0, 0, 0.3);
}

/* Keep article content visible */
.article-label.disabled {
  background: var(--color-background);
  border-color: #e0e0e0;
  cursor: not-allowed;
  opacity: 1; /* Keep full opacity so text is readable */
  pointer-events: none;
}

.dark .article-label.disabled {
  background: var(--color-background-light);
  border-color: #3a3a3a;
}

.article-label.disabled .article-name,
.article-label.disabled .article-price {
  opacity: 0.7; /* Slightly dim the text */
  text-decoration: line-through; /* Optional: strike-through effect */
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .out-of-stock-badge {
    padding: 3px 8px;
    font-size: 0.6rem;
    top: 6px;
    right: 6px;
  }
}

@media (max-width: 640px) {
  .out-of-stock-badge {
    padding: 2px 6px;
    font-size: 0.55rem;
    top: 4px;
    right: 4px;
  }
}
</style>

